import { action, observable, makeAutoObservable } from "mobx";
import { ApiHttpError } from "../interfaces/api/ApiHtppError";
import { Emote } from "../interfaces/Emote";
import CheersApiService from "../services/api/CheersApiService";
import twitch, { BitsProduct, BitsTransaction } from "twitch-ext";
import * as CheersInterface from "../interfaces/api/CheersApiService";
import { Cheer } from "../interfaces/Cheer";
import { Cheerer } from "../interfaces/Cheerer";
import { getCheerersPeriod } from "../constants/getCheerersPeriod";
import GA from "../../common/services/GA";

const SUCCESS_VIEW_VISIBILITY_TIME = 5000; // ms

interface EmotesList {
  emotes: Emote[] | null;
  error: ApiHttpError | null;
}

interface CheersList {
  cheers: Cheer[] | null;
  error: ApiHttpError | null;
}

interface CheerersList {
  cheerers: Cheerer[] | null;
  error: ApiHttpError | null;
}

interface BoughtProductData {
  emote: Emote;
  bitsAmount: number;
}

export default class CheersStore {
  constructor(public api: CheersApiService) {
    makeAutoObservable(this);
    twitch.bits.onTransactionComplete((res) => this.onTransactionComplete(res));
  }

  @observable emotesList: EmotesList = { emotes: null, error: null };
  @observable cheersList: CheersList = { cheers: null, error: null };
  @observable cheerersList: CheerersList = { cheerers: null, error: null };
  @observable cheerersListPeriod: getCheerersPeriod = getCheerersPeriod.lifetime;
  @observable selectedEmote: Emote | null = null;
  @observable selectedProduct: BitsProduct | null = null;
  @observable products: BitsProduct[] | null = null;
  @observable boughtProductData: BoughtProductData | null = null;
  @observable isSuccessViewVisible = false;
  @observable channelId: string | null = null;

  @action getEmotes(): void {
    this.emotesList = { emotes: null, error: null };
    if (this.channelId) {
      this.api
        .getEmotes({ channelId: this.channelId })
        .then((emotes) => (this.emotesList.emotes = emotes))
        .catch((error: ApiHttpError) => (this.emotesList.error = error));
    }
  }

  @action getCheers(): void {
    this.cheersList = { ...this.cheersList, error: null };
    if (this.channelId) {
      this.api
        .getCheers({ channelId: this.channelId })
        .then((cheers) => (this.cheersList.cheers = cheers))
        .catch((error: ApiHttpError) => (this.cheersList.error = error));
    }
  }

  @action getCheerers(): void {
    this.cheerersList = { ...this.cheerersList, error: null };
    if (this.channelId) {
      this.api
        .getCheerers({ channelId: this.channelId, period: this.cheerersListPeriod })
        .then((cheerers) => (this.cheerersList.cheerers = cheerers))
        .catch((error: ApiHttpError) => (this.cheerersList.error = error));
    }
  }

  @action setCheerersListPeriod(period: getCheerersPeriod): void {
    this.cheerersListPeriod = period;
  }

  @action async postCheer(cheer: CheersInterface.PostCheerForm): Promise<CheersInterface.PostCheerResponse> {
    return await this.api.postCheer(cheer);
  }

  @action cheerSelectedEmote(userId: string, username: string, receipt: string): void {
    if (this.selectedProduct && this.boughtProductData && this.channelId) {
      this.postCheer({
        emoteId: this.boughtProductData.emote.id,
        channelId: this.channelId,
        user: {
          id: userId,
          username: username,
        },
        receipt,
      });
    }
  }

  @action selectEmote(emote: Emote): void {
    this.selectedEmote = emote;
  }

  @action deselectEmote(): void {
    this.selectedEmote = null;
  }

  @action getProducts(): void {
    twitch.bits
      .getProducts()
      .then(
        (products) =>
          (this.products = [...products].sort((a, b) => parseFloat(a.cost.amount) - parseFloat(b.cost.amount)))
      );
  }

  @action selectProduct(productSku: string): void {
    this.selectedProduct = this.products?.find((product) => product.sku === productSku) ?? null;
  }

  @action deselectProduct(): void {
    this.selectedProduct = null;
  }

  @action buyProduct(): void {
    if (this.selectedProduct && this.selectedEmote) {
      this.boughtProductData = {
        emote: this.selectedEmote,
        bitsAmount: parseFloat(this.selectedProduct.cost.amount),
      };
      twitch.bits.useBits(this.selectedProduct.sku);
    }
  }

  @action onTransactionComplete(bitsTransaction: BitsTransaction): void {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - wrong interface in twitch lib
    if (bitsTransaction.initiator !== "current_user") {
      return;
    }

    this.isSuccessViewVisible = true;
    setTimeout(() => {
      this.deselectEmote();
      this.deselectProduct();
      this.isSuccessViewVisible = false;
    }, SUCCESS_VIEW_VISIBILITY_TIME);
    this.cheerSelectedEmote(bitsTransaction.userId, bitsTransaction.displayName, bitsTransaction.transactionReceipt);

    if (this.boughtProductData) {
      GA.sendEvent(
        "cheer",
        `cheer_${this.boughtProductData.emote.name}_${this.boughtProductData.bitsAmount}`,
        this.boughtProductData.emote.id,
        this.boughtProductData.bitsAmount
      );
    }
  }

  @action setChannelId(channelId: string): void {
    this.channelId = channelId;
    this.api.http.addHeader("x-twitch-channel-id", channelId); // for future monitoring reasons
  }
}
